import React, {forwardRef, memo, useCallback, useRef, useState} from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';

import {Button, EMPHASIS, FlexRow, Icon, Text, WorkSignifier} from '@forecast-it/design-system';

import GlobalSearchModal, {SEARCH_ENTITIES} from '../../../../../../navigation/header/global-search/GlobalSearchModal';
import useEventListener from '../../../../../../shared/hooks/useEventListener';
import {TimeRegistrationEntity} from '../types/TimeRegistrationEntityId';

import {TimeRegistrationModal_entity, TimeRegistrationModal_taskProject} from '../types/TimeRegistrationModalEntity';
import {isTaskTimeRegistrationAllowed} from '../../../../../util/time-registration/time-registration-settings/TimeRegistrationTaskFilter';
import {SearchSuggestion} from '../../../../../../navigation/header/global-search/types/SearchSuggestion';
import {getAllowedEntitiesText} from '../../../../../../navigation/header/global-search/formatters/FormatterUtil';
import ProjectIndicator from '../../../../project-indicator/ProjectIndicator';
import {asFragment} from '../../../../../../../relay-helpers/TSRelayConverter';
import {trackEvent} from '../../../../../../../tracking/amplitude/TrackingV2';

const SearchSelection = styled.button`
	height: 70px;
	padding: 1px 16px;
	border: 1px solid #727483;
	border-radius: 4px;

	background-color: ${({disabled}) => (disabled ? '#e7e7f3' : '#ffffff')};

	&:hover {
		background-color: #e7e7f3;
	}

	&:focus-visible {
		border-width: 2px;
		border-color: #3b94ffff;
		outline-style: none;
		padding: 0 15px;
	}
`;

const IconTitleContainer = styled.div`
	display: flex;
	flex: 1 1;
	align-items: center;
	gap: 12px;
	min-width: 0;
`;

const TypeContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	min-width: 0;
`;

const TitleContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	min-width: 0;
	width: 100%;
	text-align: left;
	align-items: flex-start;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const ClearButtonStyle = styled.div`
	div > button {
		border: none;

		&:hover {
			border: none;
		}
	}
`;

function getProjectFromOptimisticSearchResult(optimisticSearchResult): TimeRegistrationModal_taskProject {
	return asFragment<TimeRegistrationModal_taskProject>({
		companyProjectId: optimisticSearchResult.companyProjectId,
		customProjectId: optimisticSearchResult.customProjectId,
		projectColor: optimisticSearchResult.projectColor,
		name: optimisticSearchResult.projectName,
	});
}

// Home-brew optimistic formatter from raw search result to provide instant result feedback while modal does fetching and validation of selecting in the background
function formatOptimisticEntity(optimisticEntity) {
	const optimisticSearchResult = optimisticEntity.searchResult;
	if (optimisticSearchResult) {
		switch (optimisticEntity.variant) {
			case 'TASK':
				return {
					title: optimisticSearchResult.name || optimisticSearchResult.entityName, // entityName if result comes from suggestions
					subtitle: `Task in ${optimisticSearchResult.projectName}`,
					project: getProjectFromOptimisticSearchResult(optimisticSearchResult),
					companyTaskId: optimisticSearchResult.companyTaskId,
					type: 'Task',
				};
			case 'PROJECT':
				return {
					title: optimisticSearchResult.name,
					subtitle: optimisticSearchResult.clientName
						? `Project for client ${optimisticSearchResult.clientName}`
						: 'Project',
					project: getProjectFromOptimisticSearchResult(optimisticSearchResult),
					type: 'Project',
				};
			case 'INTERNAL_TIME':
				return {
					isInternalTime: true,
					title: optimisticSearchResult.entityName,
					type: 'Internal time',
				};
			default:
				return null;
		}
	}
}

export type FormattedTargetEntity = {
	isInternalTime?: boolean | null;
	title: string | null;
	subtitle?: string | null;
	project?: TimeRegistrationModal_taskProject;
	companyTaskId?: number | null;
	type: string;
};

function formatTargetEntity(targetEntity: TimeRegistrationModal_entity): FormattedTargetEntity {
	switch (targetEntity.entity) {
		case TimeRegistrationEntity.Task:
			return {
				title: targetEntity.name,
				subtitle: `Task in ${targetEntity.project?.name}`,
				project: targetEntity.project,
				companyTaskId: targetEntity.companyTaskId,
				type: 'Task',
			};
		case TimeRegistrationEntity.Project:
			return {
				title: targetEntity.name,
				subtitle: targetEntity.client?.name ? `Project for client ${targetEntity.client.name}` : 'Project',
				project: targetEntity,
				type: 'Project',
			};
		case TimeRegistrationEntity.IdleTime:
			return {
				isInternalTime: targetEntity.isInternalTime,
				title: targetEntity.name,
				type: targetEntity.isInternalTime ? 'Internal time' : 'Time off',
			};
	}
}

type GetFormattedTargetEntityInput = {
	targetEntity: TimeRegistrationModal_entity | null;
	optimisticSearchResult: unknown | null;
	targetEntityFetching: boolean;
};

function getFormattedTargetEntity({
	targetEntity,
	optimisticSearchResult,
	targetEntityFetching,
}: GetFormattedTargetEntityInput): FormattedTargetEntity | null {
	if (targetEntityFetching && optimisticSearchResult) {
		const formattedOptimisticEntity = formatOptimisticEntity(optimisticSearchResult);
		if (formattedOptimisticEntity) {
			return formattedOptimisticEntity;
		}
	}

	if (targetEntity) {
		return formatTargetEntity(targetEntity);
	}

	return null;
}

const suggestionsFilter = (searchSuggestion: SearchSuggestion, personId: number | null) => {
	if (searchSuggestion.suggestionVariant === 'TASK') {
		const personIdString = personId ? personId.toString() : '';
		const assignedPersonIds = searchSuggestion.assignedPersonIds
			? searchSuggestion.assignedPersonIds.map(personId => personId.toString())
			: [];
		return isTaskTimeRegistrationAllowed(
			searchSuggestion.statusCategory,
			personIdString,
			assignedPersonIds,
			searchSuggestion
		);
	}

	return true;
};

type TimeRegistrationModalSearchProps = {
	targetEntity: TimeRegistrationModal_entity | null;
	targetEntityFetching: boolean;
	showTaskIndicator?: boolean;
	companyId: string | null;
	personId: number | null;
	onResultSelected: (selectedItem) => void;
	clearSelected: (selectedItem) => void;
	allowedEntities: Array<SEARCH_ENTITIES>;
	trackingObject: string;
	projectId?: number;
	baseSearch?: string;
	dataCy?: string;
	disabled?: boolean;
	makeWider?: boolean;
};
const TimeRegistrationModalSearch = memo(
	forwardRef(
		(
			{
				targetEntity,
				targetEntityFetching,
				showTaskIndicator = false,
				companyId,
				personId,
				onResultSelected,
				clearSelected,
				allowedEntities,
				projectId,
				baseSearch,
				dataCy,
				trackingObject,
				disabled,
				makeWider,
			}: TimeRegistrationModalSearchProps,
			ref: React.ForwardedRef<HTMLButtonElement>
		) => {
			const intl = useIntl();
			const fallbackRef = useRef(null);
			const searchButtonRef = ref !== null && typeof ref !== 'function' ? ref : fallbackRef;

			const [searchShown, setSearchShown] = useState(false);
			const [optimisticSearchResult, setOptimisticSearchResult] = useState(null);
			const [, rerender] = useState({});

			const handleSearchSelection = useCallback(searchResult => {
				setOptimisticSearchResult(searchResult);
				onResultSelected(searchResult);
			}, []);

			const hideSearch = useCallback(() => {
				setSearchShown(false);
				searchButtonRef.current?.focus();
			}, []);

			const handleResize = useCallback(() => {
				rerender({}); // Causes modalYOffset to recalculate. Not sure if this is the most performant solution, but it works ¯\_(ツ)_/¯
			}, []);

			useEventListener('resize', handleResize, window, {throttleInterval: 20});

			const modalDimensions = {
				modalYPosition: searchButtonRef.current?.getBoundingClientRect()?.top ?? 0,
				modalXPosition: (searchButtonRef.current?.getBoundingClientRect()?.left ?? 0) - (makeWider ? 218 : 0),
				modalWidth: (searchButtonRef.current?.getBoundingClientRect()?.width ?? 610) + (makeWider ? 248 : 0),
			};

			const formattedTargetEntity = getFormattedTargetEntity({
				targetEntity,
				optimisticSearchResult,
				targetEntityFetching,
			});

			const allowedEntitiesText = getAllowedEntitiesText(allowedEntities, intl);

			const clearSelection = () => {
				trackEvent(trackingObject, 'Cleared');
				clearSelected(formattedTargetEntity);
			};

			const handleBackspace = (event: React.KeyboardEvent<HTMLElement>) => {
				if (event.key === 'Backspace') {
					clearSelection();
				}
			};

			return (
				<>
					<SearchSelection
						title={
							formattedTargetEntity
								? `${formattedTargetEntity.title} - ${formattedTargetEntity.subtitle}`
								: `Search`
						}
						ref={searchButtonRef}
						type="button"
						aria-label="Search"
						aria-details="Use this search field to look up a task, project, or track internal time."
						data-cy={dataCy}
						onClick={() => setSearchShown(true)}
						onKeyDown={handleBackspace}
						disabled={disabled}
					>
						{formattedTargetEntity ? (
							<FlexRow alignItems="center" justifyContent="space-between" gap={'s'}>
								<IconTitleContainer>
									{showTaskIndicator ? (
										<WorkSignifier
											workId={`T${formattedTargetEntity?.companyTaskId}`}
											name={formattedTargetEntity?.project?.name || undefined}
											color={formattedTargetEntity?.project?.projectColor || ''}
											outline
										/>
									) : (
										formattedTargetEntity?.project?.companyProjectId && (
											<ProjectIndicator outline project={formattedTargetEntity.project} />
										)
									)}
									{formattedTargetEntity.isInternalTime && <Icon icon="internalTime" size="l" />}
									<TitleContainer>
										<Text ellipsis>{formattedTargetEntity.title}</Text>
									</TitleContainer>
								</IconTitleContainer>
								<TypeContainer>
									<Text color="medium">{formattedTargetEntity.type}</Text>
									{!disabled ? (
										<ClearButtonStyle>
											<Button
												type={'button'}
												icon={'close'}
												emphasis={EMPHASIS.MONO}
												onPress={clearSelection}
											></Button>
										</ClearButtonStyle>
									) : null}
								</TypeContainer>
							</FlexRow>
						) : (
							<FlexRow alignItems="center" gap={'s'}>
								<Icon icon={'search'} size="m"></Icon>
								<Text color="medium">Search</Text>
							</FlexRow>
						)}
					</SearchSelection>
					{searchShown ? (
						<GlobalSearchModal
							companyId={companyId}
							personId={personId}
							isEmbedded
							modalYPosition={modalDimensions.modalYPosition}
							modalXPosition={modalDimensions.modalXPosition}
							modalWidth={modalDimensions.modalWidth}
							searchModalConfig={{
								inputPlaceholder: 'Search for a ' + allowedEntitiesText,
								allowLinks: false,
								showGiveFeedback: false,
								searchEndpoint: 'globalsearch/time-registration',
								recentSearchKey: 'recently-searched-time-registration',
								trackingObject: trackingObject,
								allowedEntities: allowedEntities,
								baseSearch: baseSearch,
								suggestionsFilter: searchSuggestion => suggestionsFilter(searchSuggestion, personId),
								projectId,
							}}
							hideSearch={hideSearch}
							onResultSelected={handleSearchSelection}
						/>
					) : null}
				</>
			);
		}
	)
);

export default TimeRegistrationModalSearch;
